import type { RefObject } from "react";
import type { Message } from "~/helpers/aiChat/chatStorage";
import type { LookResolveOutcome } from "~/helpers/aiChat/lookResolveContract";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import lookResolveContract from "~/helpers/aiChat/lookResolveContract";
import logger from "~/helpers/logger";
import readPhotoFile from "~/helpers/photoFile";
import wsId from "~/helpers/wsId";
import { useAppDispatch } from "~/hooks/reduxHooks";
import resolveLookFrame from "~/services/lookResolve";
import { registerActiveEntity } from "~/store/fernieActionSlice";

export type LookIngressPhase = "idle" | "resolving";

export interface UseFernieLookIngressOptions {
	appendMessages: (messages: Message[]) => void;
	chatId: string;
	enabled: boolean;
	loading: boolean;
	replaceAssistantMessage: (messageId: string, content: string) => void;
	sessionId: string | null;
}

type CameraChangeEvent = Pick<React.ChangeEvent<HTMLInputElement>, "target">;

export interface UseFernieLookIngressReturn {
	busy: boolean;
	cameraInputRef: RefObject<HTMLInputElement | null>;
	handleCameraChange: (event: CameraChangeEvent) => Promise<void>;
	openCamera: () => void;
	phase: LookIngressPhase;
}

const useFernieLookIngress = ({
	appendMessages,
	chatId,
	enabled,
	loading,
	replaceAssistantMessage,
	sessionId
}: UseFernieLookIngressOptions): UseFernieLookIngressReturn => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const cameraInputRef = useRef<HTMLInputElement | null>(null);
	const inFlightRef = useRef(false);
	const chatIdRef = useRef(chatId);
	const [phase, setPhase] = useState<LookIngressPhase>("idle");
	const busy = phase === "resolving" || loading;

	useEffect(() => {
		chatIdRef.current = chatId;
	}, [chatId]);

	const openCamera = useCallback(() => {
		if (!enabled || busy) return;
		cameraInputRef.current?.click();
	}, [busy, enabled]);

	const describeOutcome = useCallback(
		(outcome: LookResolveOutcome): string => {
			if (outcome.kind === "resolved") {
				const name = outcome.entity.displayName.trim() || outcome.entity.entityId;
				return t("fernieChat.look.found", { name });
			}
			if (outcome.kind === "ambiguous") {
				return lookResolveContract.listCandidates(
					outcome,
					outcome.clarifyingQuestion ?? t("fernieChat.look.ambiguousFallback")
				);
			}
			return outcome.clarifyingQuestion ?? t("fernieChat.look.unresolvedFallback");
		},
		[t]
	);

	const handleCameraChange = useCallback(
		async (event: CameraChangeEvent) => {
			const file = event.target.files?.[0] ?? null;
			event.target.value = "";
			if (!file || !enabled || busy || inFlightRef.current) return;

			inFlightRef.current = true;
			const startedInChatId = chatIdRef.current;
			try {
				const read = await readPhotoFile(file);
				if (read.errorCode) {
					appendMessages([{ content: t("fernieChat.look.readFailed"), role: "assistant" }]);
					return;
				}

				const progressId = wsId();
				appendMessages([
					{ content: t("fernieChat.look.userSnap"), role: "user" },
					{ content: t("fernieChat.look.progress"), id: progressId, role: "assistant", streaming: true }
				]);
				setPhase("resolving");

				let reply: string;
				try {
					const outcome = await resolveLookFrame({
						imageBase64: read.value.image,
						ingressSource: lookResolveContract.LOOK_INGRESS_SOURCE,
						mimeType: read.value.mime,
						sessionId
					});
					if (chatIdRef.current !== startedInChatId) return;
					if (outcome.kind === "resolved")
						dispatch(registerActiveEntity(lookResolveContract.toEntityContext(outcome.entity)));
					reply = describeOutcome(outcome);
				} catch (error: unknown) {
					logger.error("Look resolve failed:", error);
					reply = t(
						lookResolveContract.isLookUnavailableError(error)
							? "fernieChat.look.unavailable"
							: "fernieChat.look.resolveFailed"
					);
				}

				if (chatIdRef.current !== startedInChatId) return;
				replaceAssistantMessage(progressId, reply);
			} finally {
				inFlightRef.current = false;
				setPhase("idle");
			}
		},
		[appendMessages, busy, describeOutcome, enabled, replaceAssistantMessage, sessionId, t]
	);

	return {
		busy,
		cameraInputRef,
		handleCameraChange,
		openCamera,
		phase
	};
};

export default useFernieLookIngress;
